import React, { useEffect } from 'react'
import { Provider, useDispatch } from 'react-redux'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { store } from './store'
import { setUser, setLoading } from './store/authSlice'
import authService from './services/authService'
import Landing from './pages/Landing'
import Product from './pages/Product'
import Solutions from './pages/Solutions'
import Resources from './pages/Resources'
import ForBusiness from './pages/ForBusiness'
import Pricing from './pages/Pricing'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AuthCallback from './pages/AuthCallback'
import Dashboard from './pages/Dashboard'
import RestaurantsList from './pages/RestaurantsList'
import Around from './pages/Around'
import Account from './pages/Account'
import RestaurantDetail from './pages/RestaurantDetail'
import RestaurantEdit from './pages/RestaurantEdit'
import KitchenDisplay from './pages/KitchenDisplay'
import MenuDetail from './pages/MenuDetail'
import RestaurantCosts from './pages/RestaurantCosts'
import RestaurantIngredients from './pages/RestaurantIngredients'
import RestaurantOrders from './pages/RestaurantOrders'
import OrderDetail from './pages/OrderDetail'
import StaffInvitations from './pages/StaffInvitations'
import HowIEatDetail from './pages/HowIEatDetail'
import HowIEatLogMeal from './pages/HowIEatLogMeal'
import HowIEatSettings from './pages/HowIEatSettings'
import HowIEatProgress from './pages/HowIEatProgress'
import ProtectedRoute from './components/ProtectedRoute'
import Navigation from './components/Navigation'
import { LanguageProvider } from './hooks/use-i18n'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
})

// Component to handle authentication restoration
const AuthInitializer: React.FC = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { user, token } = authService.restoreAuth()
        if (user && token) {
          dispatch(setUser(user))
        }
      } catch (error) {
        console.error('Auth restoration failed:', error)
      } finally {
        dispatch(setLoading(false))
      }
    }

    initializeAuth()
  }, [dispatch])

  return null
}

const AppContent: React.FC = () => {
  const location = useLocation()
  const isLanding = location.pathname === '/'

  return (
    <div className="bg-[#111111] min-h-screen text-white overflow-x-hidden">
      <Navigation />
      <div className={isLanding ? '' : 'pt-14 sm:pt-16'}>
        <Routes>
        <Route path="/" element={<Landing />} />
            <Route path="/product" element={<Product />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/for-business" element={<ForBusiness />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurants" 
              element={
                <ProtectedRoute>
                  <RestaurantsList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/around" 
              element={
                <ProtectedRoute>
                  <Around />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account" 
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId/edit" 
              element={
                <ProtectedRoute>
                  <RestaurantEdit />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId/kds" 
              element={
                <ProtectedRoute>
                  <KitchenDisplay />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId" 
              element={
                <ProtectedRoute>
                  <RestaurantDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/menu/:menuId" 
              element={
                <ProtectedRoute>
                  <MenuDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId/costs" 
              element={
                <ProtectedRoute>
                  <RestaurantCosts />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId/ingredients" 
              element={
                <ProtectedRoute>
                  <RestaurantIngredients />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId/orders" 
              element={
                <ProtectedRoute>
                  <RestaurantOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/restaurant/:restaurantId/orders/:orderId" 
              element={
                <ProtectedRoute>
                  <OrderDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/how-i-eat/:spaceId/progress" 
              element={
                <ProtectedRoute>
                  <HowIEatProgress />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/how-i-eat/:spaceId/log-meal/:mealId?" 
              element={
                <ProtectedRoute>
                  <HowIEatLogMeal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/how-i-eat/:spaceId/settings" 
              element={
                <ProtectedRoute>
                  <HowIEatSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/how-i-eat/:spaceId" 
              element={
                <ProtectedRoute>
                  <HowIEatDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/staff-invitations" 
              element={
                <ProtectedRoute>
                  <StaffInvitations />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
          },
        }}
      />
    </div>
  )
}

function App() {
  return (
    <Provider store={store}>
      <LanguageProvider>
      <QueryClientProvider client={queryClient}>
      <Router>
        <AuthInitializer />
        <AppContent />
      </Router>
      </QueryClientProvider>
      </LanguageProvider>
    </Provider>
  )
}

export default App
